import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Mail, Lock, User, ArrowRight, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { practitionerApplicationApi, type PractitionerApplicationRequest } from '../services/practitionerApplicationApi';
import { LegalPrivacyLabel, LegalTermsLabel } from '../components/LegalConsentLabels';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';

export function BecomePartnerPage() {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const [formData, setFormData] = useState<PractitionerApplicationRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    disciplines: [],
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptPrivacy: false,
    message: '',
    portfolioUrl: '',
  });

  useEffect(() => {
    setError(null);
  }, [currentStep]);

  const handleInputChange = (field: keyof PractitionerApplicationRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const toggleDiscipline = (d: 'TATTOO' | 'PIERCING') => {
    setFormData((prev) => {
      const has = prev.disciplines.includes(d);
      const disciplines = has ? prev.disciplines.filter((x) => x !== d) : [...prev.disciplines, d];
      return { ...prev, disciplines };
    });
    if (validationErrors.disciplines) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.disciplines;
        return next;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const errs: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) errs.firstName = 'Required';
      if (!formData.lastName.trim()) errs.lastName = 'Required';
      if (!formData.email.trim()) errs.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email';
      if (!phoneDisplay.trim()) errs.phone = 'Required';
      else if (!phoneIsValid || !phoneE164) errs.phone = 'Invalid phone number';
      if (!formData.disciplines.length) errs.disciplines = 'Select at least one';
    }

    if (step === 2) {
      if (!formData.password) errs.password = 'Required';
      if (!formData.confirmPassword) errs.confirmPassword = 'Required';
      if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
        errs.confirmPassword = 'Passwords do not match';
      }
    }

    if (step === 3) {
      if (!formData.acceptTerms) errs.acceptTerms = 'Required';
      if (!formData.acceptPrivacy) errs.acceptPrivacy = 'Required';
    }

    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) setCurrentStep((s) => Math.min(3, s + 1));
  };

  const prevStep = () => setCurrentStep((s) => Math.max(1, s - 1));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(3) || !phoneE164) return;

    setLoading(true);
    setError(null);
    try {
      const resp = await practitionerApplicationApi.apply({
        ...formData,
        phone: phoneE164,
        email: formData.email.trim().toLowerCase(),
        message: formData.message?.trim() || undefined,
        portfolioUrl: formData.portfolioUrl?.trim() || undefined,
      });
      if (resp.success) {
        setSuccess(true);
      } else {
        setError(resp.message || 'Application failed');
      }
    } catch (err: any) {
      setError(err?.message || 'Application failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mx-auto">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-display font-bold text-textPrimary">
            Demande envoyée
          </h2>
          <p className="text-sm text-textSecondary">
            Votre demande “Devenir partenaire” est en attente de validation. Vous serez contacté rapidement.
          </p>
          <div className="space-y-3">
            <Link to="/" className="btn-primary w-full inline-flex justify-center">
              Retour à l’accueil
            </Link>
            <Link to="/login" className="btn-secondary w-full inline-flex justify-center">
              Se connecter (client)
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-300 to-accent-500 rounded-2xl mx-auto mb-6">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-display font-bold text-textPrimary">
            Devenir partenaire
          </h2>
          <p className="mt-2 text-sm text-textSecondary">
            Déposez votre demande (tatoueur et/ou perceur). Validation par l’équipe avant activation.
          </p>

          <div className="mt-6 flex items-center justify-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step}
                </div>
                {step < 3 && <div className={`w-8 h-0.5 ${step < currentStep ? 'bg-primary-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Prénom</label>
                  <input
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`input-field ${validationErrors.firstName ? 'border-red-300 bg-red-50' : ''}`}
                  />
                  {validationErrors.firstName && <p className="mt-1 text-sm text-red-600">{validationErrors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Nom</label>
                  <input
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`input-field ${validationErrors.lastName ? 'border-red-300 bg-red-50' : ''}`}
                  />
                  {validationErrors.lastName && <p className="mt-1 text-sm text-red-600">{validationErrors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`input-field pl-10 ${validationErrors.email ? 'border-red-300 bg-red-50' : ''}`}
                  />
                </div>
                {validationErrors.email && <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Téléphone</label>
                <InternationalPhoneInput
                  value={phoneDisplay}
                  country={phoneCountry}
                  required
                  onChange={(result) => {
                    setPhoneDisplay(result.displayValue);
                    setPhoneCountry(result.country);
                    setPhoneE164(result.e164Value);
                    setPhoneIsValid(result.isValid);
                    setFormData((prev) => ({ ...prev, phone: result.displayValue }));
                    if (result.isValid && validationErrors.phone) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.phone;
                        return next;
                      });
                    }
                  }}
                  selectClassName="input-field"
                  inputClassName={`input-field ${validationErrors.phone ? 'border-red-300 bg-red-50' : ''}`}
                  error={validationErrors.phone}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Je suis</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white">
                    <input
                      type="checkbox"
                      checked={formData.disciplines.includes('TATTOO')}
                      onChange={() => toggleDiscipline('TATTOO')}
                    />
                    <span>Tatoueur</span>
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white">
                    <input
                      type="checkbox"
                      checked={formData.disciplines.includes('PIERCING')}
                      onChange={() => toggleDiscipline('PIERCING')}
                    />
                    <span>Perceur</span>
                  </label>
                </div>
                {validationErrors.disciplines && <p className="mt-1 text-sm text-red-600">{validationErrors.disciplines}</p>}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Mot de passe</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`input-field pl-10 pr-12 ${validationErrors.password ? 'border-red-300 bg-red-50' : ''}`}
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword((v) => !v)}>
                    {showPassword ? <EyeOff className="h-5 w-5 text-textSecondary" /> : <Eye className="h-5 w-5 text-textSecondary" />}
                  </button>
                </div>
                {validationErrors.password && <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Confirmer</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`input-field pl-10 pr-12 ${validationErrors.confirmPassword ? 'border-red-300 bg-red-50' : ''}`}
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword((v) => !v)}>
                    {showConfirmPassword ? <EyeOff className="h-5 w-5 text-textSecondary" /> : <Eye className="h-5 w-5 text-textSecondary" />}
                  </button>
                </div>
                {validationErrors.confirmPassword && <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Message (optionnel)</label>
                <textarea
                  value={formData.message || ''}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="input-field"
                  rows={3}
                  placeholder="Quelques mots sur votre expérience..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">Portfolio (URL optionnelle)</label>
                <input
                  value={formData.portfolioUrl || ''}
                  onChange={(e) => handleInputChange('portfolioUrl', e.target.value)}
                  className="input-field"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.acceptTerms}
                      onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-sm text-textSecondary">J’accepte les conditions d’utilisation.</span>
                  </label>
                  {validationErrors.acceptTerms && <p className="text-sm text-red-600">{validationErrors.acceptTerms}</p>}

                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.acceptPrivacy}
                      onChange={(e) => handleInputChange('acceptPrivacy', e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-sm text-textSecondary">J’accepte la politique de confidentialité.</span>
                  </label>
                  {validationErrors.acceptPrivacy && <p className="text-sm text-red-600">{validationErrors.acceptPrivacy}</p>}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            {currentStep > 1 ? (
              <button type="button" onClick={prevStep} className="btn-secondary flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Précédent
              </button>
            ) : <div />}

            {currentStep < 3 ? (
              <button type="button" onClick={nextStep} className="btn-primary flex items-center gap-2">
                Suivant
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                {loading ? 'Envoi...' : 'Envoyer la demande'}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="text-center">
            <p className="text-sm text-textSecondary">
              Déjà client ? <Link to="/login" className="fo-link font-medium">Se connecter</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BecomePartnerPage;
